import { motion } from "framer-motion";
import { Mic, BookOpen, Users, Youtube } from "lucide-react";

const platforms = [
  { name: "Unacademy", role: "Int'l Taxation · CA Final (formerly)", color: "#16D1C8" },
  { name: "Physics Wallah", role: "Income Tax Faculty", color: "#FF2D7A" },
  { name: "Bajaj Capital ICOFP", role: "CFP Level 2", color: "#FF8A3D" },
  { name: "ICAI", role: "Sessions & PD Programmes", color: "#0EE6D9" },
  { name: "Corporate Trainings", role: "Workshops & Speaker Sessions", color: "#E91E63" },
  { name: "YouTube", role: "Simplifying tax for the world", color: "#FF8A3D" },
];

const formats = [
  { icon: Mic, label: "Keynote & Speaker Sessions" },
  { icon: BookOpen, label: "Faculty for CA Final & CFP" },
  { icon: Users, label: "Corporate Training Workshops" },
  { icon: Youtube, label: "Online Content & Courses" },
];

export const Teaching = () => {
  return (
    <section id="teaching" data-testid="teaching-section" className="relative section-pad overflow-hidden">
      <div className="shape-blob w-[420px] h-[420px] -left-32 top-20 opacity-25"
           style={{ background: "radial-gradient(circle, #16D1C8 0%, transparent 70%)" }} />

      <div className="container-x relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="max-w-3xl"
        >
          <div className="label-eyebrow mb-5">Faculty engagements</div>
          <h2 data-testid="teaching-title" className="font-heading font-bold text-4xl md:text-5xl lg:text-6xl leading-[1.05]">
            Faculty. Speaker. <br />
            <span className="gradient-text-cool">Mentor.</span>
          </h2>
          <p className="mt-6 text-white/65 max-w-2xl">
            Textbooks teach the law. Real practice teaches judgment. That is what I bring to every session.
          </p>
        </motion.div>

        <div className="mt-14 grid lg:grid-cols-[1.3fr,1fr] gap-6">
          {/* Platforms */}
          <div className="grid sm:grid-cols-2 gap-4">
            {platforms.map((pl, i) => (
              <motion.div
                key={pl.name}
                data-testid={`platform-${i}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                className="glass p-6 group hover:-translate-y-1 transition-all duration-500 relative overflow-hidden"
              >
                <div className="absolute top-0 left-0 right-0 h-px" style={{ background: `linear-gradient(90deg, transparent, ${pl.color}, transparent)` }} />
                <div className="flex items-center gap-4">
                  <div className="w-11 h-11 rounded-xl grid place-items-center font-heading font-bold text-base"
                       style={{ background: `linear-gradient(135deg, ${pl.color}, ${pl.color}AA)`, color: "#05091A" }}>
                    {pl.name.charAt(0)}
                  </div>
                  <div>
                    <div className="font-heading font-semibold text-white text-lg leading-tight">{pl.name}</div>
                    <div className="text-xs text-white/55 mt-0.5">{pl.role}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Formats */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6 }}
            className="glass p-8 relative overflow-hidden"
            data-testid="teaching-formats"
          >
            <div className="absolute -bottom-24 -right-24 w-56 h-56 rounded-full opacity-30 blur-3xl"
                 style={{ background: "#FF2D7A" }} />
            <div className="relative z-10">
              <div className="label-eyebrow">Engagements</div>
              <h3 className="font-heading font-bold text-2xl mt-3">Available formats</h3>
              <ul className="mt-6 space-y-4">
                {formats.map((f) => (
                  <li key={f.label} className="flex items-center gap-3 text-white/85">
                    <div className="w-10 h-10 grid place-items-center rounded-xl border border-white/10 bg-white/[0.03]">
                      <f.icon size={16} className="text-teal-400" />
                    </div>
                    <span className="text-sm md:text-base">{f.label}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-8 pt-6 border-t border-white/10 text-sm text-white/55">
                Looking for a corporate speaker or visiting faculty? Reach out below.
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
